import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CAT_COLORS = {
  loading_unloading: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  daily_labour: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  tiles_work: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  house_repair: "bg-purple-500/20 text-purple-300 border-purple-500/40",
};

const PAY_DOT = { successful: "bg-emerald-400", pending: "bg-amber-400", failed: "bg-red-400", processing: "bg-amber-400" };

const fmt = (d) => d.toISOString().slice(0, 10);
const parse = (s) => new Date(s + "T00:00:00");

export default function AdminCalendar() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState({ by_date: {}, count: 0 });
  const [selected, setSelected] = useState(null);

  const range = useMemo(() => {
    const d = new Date(cursor);
    if (view === "month") {
      const first = new Date(d.getFullYear(), d.getMonth(), 1);
      const start = new Date(first); start.setDate(first.getDate() - first.getDay()); // Sunday-start grid
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const end = new Date(last); end.setDate(last.getDate() + (6 - last.getDay()));
      return { start, end };
    } else if (view === "week") {
      const start = new Date(d); start.setDate(d.getDate() - d.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { start, end };
    }
    return { start: d, end: d };
  }, [cursor, view]);

  useEffect(() => {
    api.get("/admin/calendar", { params: { start: fmt(range.start), end: fmt(range.end) } })
      .then(r => setData(r.data))
      .catch(() => {});
  }, [range]);

  const shift = (delta) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + 7 * delta);
    else d.setDate(d.getDate() + delta);
    setCursor(d);
  };

  const days = useMemo(() => {
    const out = [];
    for (let d = new Date(range.start); d <= range.end; d = new Date(d.getTime() + 86400000)) out.push(new Date(d));
    return out;
  }, [range]);

  const title = view === "day"
    ? cursor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><CalIcon className="text-orange-400"/>Operations Calendar</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-700" data-testid="cal-view-toggle">
            {["month", "week", "day"].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase ${view === v ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                data-testid={`cal-view-${v}`}>{v}</button>
            ))}
          </div>
          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
          <Button size="icon" variant="outline" className="border-slate-700" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft size={16}/></Button>
          <div className="px-3 font-mono text-sm text-slate-300 min-w-[180px] text-center" data-testid="cal-title">{title}</div>
          <Button size="icon" variant="outline" className="border-slate-700" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight size={16}/></Button>
        </div>
      </div>

      {view === "month" && (
        <Card className="admin-card p-3">
          <div className="grid grid-cols-7 gap-1 text-[11px] uppercase tracking-widest text-slate-500 font-bold px-1 pb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map(d => {
              const key = fmt(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const items = data.by_date[key] || [];
              const isToday = key === fmt(new Date());
              return (
                <div key={key} data-testid={`cal-day-${key}`}
                  className={`min-h-[88px] rounded-md p-1.5 border ${inMonth ? "border-slate-700 bg-slate-900/50" : "border-slate-800 bg-slate-900/20 opacity-50"} ${isToday ? "ring-1 ring-orange-500" : ""}`}>
                  <div className={`text-xs font-bold mb-1 ${isToday ? "text-orange-400" : "text-slate-300"}`}>{d.getDate()}</div>
                  <div className="space-y-0.5">
                    {items.slice(0, 3).map(b => (
                      <button key={b.id} onClick={() => setSelected(b)}
                        className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] font-semibold truncate border ${CAT_COLORS[b.category]} hover:brightness-125`}
                        data-testid={`cal-event-${b.code}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${PAY_DOT[b.payment_status] || "bg-slate-400"}`}/>
                        {b.start_time} {b.code} ({b.assigned_count}/{b.worker_count})
                      </button>
                    ))}
                    {items.length > 3 && <div className="text-[10px] text-slate-500 pl-1">+{items.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "week" && (
        <Card className="admin-card p-3 overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[800px]">
            {days.map(d => {
              const key = fmt(d);
              const items = data.by_date[key] || [];
              return (
                <div key={key} className="border border-slate-700 rounded-md p-2 min-h-[280px]" data-testid={`cal-week-day-${key}`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{d.toLocaleDateString("en-IN", {weekday:"short"})}</div>
                  <div className="text-lg font-bold">{d.getDate()}</div>
                  <div className="mt-2 space-y-1">
                    {items.map(b => (
                      <button key={b.id} onClick={() => setSelected(b)}
                        className={`w-full text-left px-2 py-1 rounded text-[11px] font-semibold border ${CAT_COLORS[b.category]} hover:brightness-125`}>
                        <div className="flex justify-between"><span>{b.start_time}</span><span className="font-mono">{b.assigned_count}/{b.worker_count}</span></div>
                        <div className="truncate opacity-90">{b.code}</div>
                      </button>
                    ))}
                    {items.length === 0 && <div className="text-xs text-slate-600">—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "day" && (
        <Card className="admin-card p-5">
          <div className="space-y-2">
            {Array.from({length: 15}).map((_, i) => {
              const hr = i + 7;
              const items = (data.by_date[fmt(cursor)] || []).filter(b => parseInt((b.start_time || "9:00").split(":")[0]) === hr);
              return (
                <div key={hr} className="flex gap-3 border-b border-slate-800 pb-2" data-testid={`cal-hour-${hr}`}>
                  <div className="w-16 text-slate-500 font-mono text-sm pt-1">{hr.toString().padStart(2,"0")}:00</div>
                  <div className="flex-1 flex flex-wrap gap-2">
                    {items.length === 0 ? <div className="text-slate-700 text-xs pt-1">—</div> : items.map(b => (
                      <button key={b.id} onClick={() => setSelected(b)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${CAT_COLORS[b.category]} hover:brightness-125`}>
                        <div className="font-mono">{b.code} · {b.assigned_count}/{b.worker_count}</div>
                        <div className="opacity-80">{b.customer_name} · ₹{b.amount}</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-mono">{selected?.code}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <Row k="Service" v={selected.category}/>
              <Row k="Schedule" v={`${selected.scheduled_date} ${selected.start_time} (${selected.duration_hours}h)`}/>
              <Row k="Workers" v={`${selected.assigned_count}/${selected.worker_count}`}/>
              <Row k="Payment" v={selected.payment_status}/>
              <Row k="Status" v={selected.status}/>
              <Row k="Customer" v={selected.customer_name}/>
              <Row k="Amount" v={`₹${selected.amount}`}/>
              <Row k="Location" v={selected.location}/>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({k, v}) => <div className="flex justify-between gap-3"><span className="text-slate-400">{k}</span><span className="font-mono text-slate-100">{v}</span></div>;
